import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Loader2, Check, X, Save, Image as ImageIcon, TicketCheck, Hourglass, Ban, IndianRupee, Eye, CalendarDays, Trash2, Download,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import { STATUS_META, SERVICES } from "@/lib/services";
import { CapacityCalendar } from "@/components/CapacityCalendar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const serviceShort = (id) => SERVICES.find((s) => s.id === id)?.short || id;

export default function AdminDashboard() {
  const { admin } = useAdmin();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [viewShot, setViewShot] = useState(null);
  const [acting, setActing] = useState(null);

  const [upiId, setUpiId] = useState("");
  const [savingUpi, setSavingUpi] = useState(false);

  const [caps, setCaps] = useState([]);
  const [capForm, setCapForm] = useState({ service_id: "", date: "", limit: "" });
  const [savingCap, setSavingCap] = useState(false);
  const [waitlist, setWaitlist] = useState([]);

  useEffect(() => {
    if (admin === null) navigate("/admin");
  }, [admin, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s, u, cp, wl] = await Promise.all([
        api.get("/admin/bookings", { params: { status: filter } }),
        api.get("/admin/stats"),
        api.get("/settings/upi"),
        api.get("/admin/capacity"),
        api.get("/admin/waitlist"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      setUpiId(u.data.upi_id);
      setCaps(cp.data);
      setWaitlist(wl.data);
    } catch { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { if (admin) load(); }, [admin, load]);

  const act = async (ref, action) => {
    setActing(ref + action);
    try {
      await api.post(`/admin/bookings/${ref}/${action}`);
      toast.success(`Booking ${action === "confirm" ? "confirmed" : "cancelled"}`);
      load();
    } catch { toast.error("Action failed"); }
    finally { setActing(null); }
  };

  const saveUpi = async () => {
    if (!upiId.trim()) { toast.error("UPI ID cannot be empty"); return; }
    setSavingUpi(true);
    try {
      await api.put("/admin/settings/upi", { upi_id: upiId.trim(), payee_name: "TTD Devasthanams" });
      toast.success("UPI ID updated — QR code refreshed for all pilgrims");
    } catch { toast.error("Could not update UPI ID"); }
    finally { setSavingUpi(false); }
  };

  const saveCap = async () => {
    if (!capForm.service_id || !capForm.date || !capForm.limit) { toast.error("Select service, date and seat limit"); return; }
    setSavingCap(true);
    try {
      await api.put("/admin/capacity", { service_id: capForm.service_id, date: capForm.date, limit: Number(capForm.limit) });
      toast.success("Daily seat limit saved");
      setCapForm({ service_id: "", date: "", limit: "" });
      load();
    } catch { toast.error("Could not save limit"); }
    finally { setSavingCap(false); }
  };

  const delCap = async (service_id, date) => {
    try { await api.delete("/admin/capacity", { params: { service_id, date } }); toast.success("Limit removed"); load(); }
    catch { toast.error("Could not remove limit"); }
  };

  if (!admin) return null;

  const statCards = [
    { label: "Total Bookings", value: stats?.total ?? 0, icon: TicketCheck, color: "#7A0016" },
    { label: "Pending Approval", value: stats?.by_status?.pending ?? 0, icon: Hourglass, color: "#E65100" },
    { label: "Confirmed", value: stats?.by_status?.confirmed ?? 0, icon: Check, color: "#1B5E20" },
    { label: "Collected (₹)", value: (stats?.revenue ?? 0).toLocaleString("en-IN"), icon: IndianRupee, color: "#DAA520" },
  ];

  return (
    <div className="App min-h-screen bg-[#FFFBF5]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="font-cinzel text-3xl font-bold text-amber-950">Admin Dashboard</h1>
        <p className="text-slate-500 text-sm">Manage bookings, verify payments and configure UPI.</p>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {statCards.map((c) => (
            <div key={c.label} className="bg-white border border-amber-200 rounded-xl p-5 shadow-sm" data-testid={`stat-${c.label.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{c.label}</span>
                <c.icon className="w-5 h-5" style={{ color: c.color }} />
              </div>
              <div className="font-cinzel text-2xl font-bold mt-2" style={{ color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>

        {/* UPI manager */}
        <div className="bg-white border border-emerald-200 rounded-xl p-5 shadow-sm mt-6">
          <div className="flex items-center gap-2 mb-3">
            <IndianRupee className="w-5 h-5 text-emerald-600" />
            <h2 className="font-semibold text-amber-950">Payment UPI ID</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <Label>Live UPI ID (shown to all pilgrims)</Label>
              <Input value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="name@bank" data-testid="admin-upi-id-input" className="font-mono" />
            </div>
            <Button onClick={saveUpi} disabled={savingUpi} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-upi-save-btn">
              {savingUpi ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Update UPI</>}
            </Button>
          </div>
        </div>

        {/* Slot capacity manager */}
        <div className="bg-white border border-amber-200 rounded-xl p-5 shadow-sm mt-6">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="w-5 h-5 text-[#E65100]" />
            <h2 className="font-semibold text-amber-950">Slot Capacity — Daily Seat Limits</h2>
          </div>
          <CapacityCalendar onChange={load} />
          <div className="mt-6 pt-4 border-t border-amber-100 text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Quick add (single date)</div>
          <div className="grid sm:grid-cols-4 gap-3 sm:items-end">
            <div className="sm:col-span-2">
              <Label>Service</Label>
              <Select value={capForm.service_id} onValueChange={(v) => setCapForm((f) => ({ ...f, service_id: v }))}>
                <SelectTrigger data-testid="capacity-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent>
                  {SERVICES.filter((s) => !s.hidden).map((s) => <SelectItem key={s.id} value={s.id}>{s.short}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={capForm.date} onChange={(e) => setCapForm((f) => ({ ...f, date: e.target.value }))} data-testid="capacity-date-input" />
            </div>
            <div>
              <Label>Seat Limit</Label>
              <Input type="number" min="0" value={capForm.limit} onChange={(e) => setCapForm((f) => ({ ...f, limit: e.target.value }))} placeholder="e.g. 500" data-testid="capacity-limit-input" />
            </div>
          </div>
          <Button onClick={saveCap} disabled={savingCap} className="mt-3 bg-[#E65100] hover:bg-[#BF360C] text-white" data-testid="capacity-save-btn">
            {savingCap ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save Limit</>}
          </Button>

          {caps.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="text-left py-2">Service</th><th className="text-left py-2">Date</th><th className="text-left py-2">Limit</th><th className="text-left py-2">Booked</th><th className="text-left py-2">Remaining</th><th></th></tr>
                </thead>
                <tbody>
                  {caps.map((cp) => (
                    <tr key={cp.service_id + cp.date} className="border-t border-amber-100" data-testid={`cap-row-${cp.service_id}-${cp.date}`}>
                      <td className="py-2 font-medium">{serviceShort(cp.service_id)}</td>
                      <td className="py-2">{cp.date}</td>
                      <td className="py-2 font-semibold">{cp.limit}</td>
                      <td className="py-2">{cp.booked}</td>
                      <td className={`py-2 font-bold ${cp.remaining > 0 ? "text-emerald-700" : "text-red-600"}`}>{cp.remaining}</td>
                      <td className="py-2 text-right">
                        <button onClick={() => delCap(cp.service_id, cp.date)} className="text-red-500 hover:text-red-700" data-testid={`cap-delete-${cp.service_id}-${cp.date}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Waitlist */}
        {waitlist.length > 0 && (
          <div className="bg-white border border-amber-200 rounded-xl p-5 shadow-sm mt-6" data-testid="waitlist-card">
            <div className="flex items-center gap-2 mb-3">
              <Hourglass className="w-5 h-5 text-[#E65100]" />
              <h2 className="font-semibold text-amber-950">Waitlist ({waitlist.length})</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="text-left py-2">Service</th><th className="text-left py-2">Date</th><th className="text-left py-2">Name</th><th className="text-left py-2">Mobile</th><th className="text-left py-2">Status</th></tr>
                </thead>
                <tbody>
                  {waitlist.map((w) => (
                    <tr key={w.id} className="border-t border-amber-100" data-testid={`waitlist-row-${w.id}`}>
                      <td className="py-2 font-medium">{serviceShort(w.service_id)}</td>
                      <td className="py-2">{w.date}</td>
                      <td className="py-2 font-medium">{w.name}</td>
                      <td className="py-2">{w.mobile}</td>
                      <td className="py-2">{w.notified ? <span className="text-emerald-700 font-semibold">Notified</span> : <span className="text-amber-700 font-semibold">Waiting</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-semibold text-amber-950">Bookings</h2>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList data-testid="booking-filter-tabs">
              <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
              <TabsTrigger value="pending" data-testid="filter-pending">Pending</TabsTrigger>
              <TabsTrigger value="confirmed" data-testid="filter-confirmed">Confirmed</TabsTrigger>
              <TabsTrigger value="cancelled" data-testid="filter-cancelled">Cancelled</TabsTrigger>
              <TabsTrigger value="awaiting_payment" data-testid="filter-awaiting">Unpaid</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Table */}
        <div className="mt-4 bg-white border border-amber-200 rounded-xl shadow-sm overflow-x-auto">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E65100]" /></div>
          ) : bookings.length === 0 ? (
            <div className="py-16 text-center text-slate-400">No bookings found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FFF4E5] text-amber-900 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Reference</th>
                  <th className="text-left px-4 py-3">Service</th>
                  <th className="text-left px-4 py-3">Pilgrim</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Payment</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => {
                  const meta = STATUS_META[b.status] || STATUS_META.pending;
                  return (
                    <tr key={b.reference} className="border-t border-amber-100 hover:bg-amber-50/50" data-testid={`row-${b.reference}`}>
                      <td className="px-4 py-3 font-mono font-semibold text-[#7A0016]">{b.reference}</td>
                      <td className="px-4 py-3">{b.service_name}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{b.lead_name}</div>
                        <div className="text-xs text-slate-400">{b.mobile}</div>
                      </td>
                      <td className="px-4 py-3">{b.booking_date}</td>
                      <td className="px-4 py-3 font-semibold text-[#E65100]">₹{Number(b.amount).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3">
                        {b.payment?.screenshot ? (
                          <button onClick={() => setViewShot(b)} className="flex items-center gap-1 text-xs text-emerald-700 font-semibold hover:underline" data-testid={`view-payment-${b.reference}`}>
                            <Eye className="w-4 h-4" /> View
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400 flex items-center gap-1"><ImageIcon className="w-4 h-4" /> None</span>
                        )}
                      </td>
                      <td className="px-4 py-3"><span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${meta.color}`}>{meta.label}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {b.status === "confirmed" && (
                            <a href={`${BACKEND}/api/bookings/${b.reference}/ticket`} className="text-[#7A0016] hover:text-[#50000E] h-8 flex items-center" title="Download e-Ticket" data-testid={`download-ticket-${b.reference}`}>
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                          <Button size="sm" disabled={b.status === "confirmed" || acting === b.reference + "confirm"} onClick={() => act(b.reference, "confirm")} className="bg-green-600 hover:bg-green-700 text-white h-8" data-testid={`admin-confirm-btn-${b.reference}`}>
                            {acting === b.reference + "confirm" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          </Button>
                          <Button size="sm" disabled={b.status === "cancelled" || acting === b.reference + "cancel"} onClick={() => act(b.reference, "cancel")} className="bg-red-600 hover:bg-red-700 text-white h-8" data-testid={`admin-cancel-btn-${b.reference}`}>
                            {acting === b.reference + "cancel" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Screenshot lightbox */}
      <Dialog open={!!viewShot} onOpenChange={(o) => !o && setViewShot(null)}>
        <DialogContent className="max-w-md" data-testid="payment-screenshot-dialog">
          <DialogHeader><DialogTitle className="font-cinzel text-amber-950">Payment Proof — {viewShot?.reference}</DialogTitle></DialogHeader>
          {viewShot?.payment && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Transaction ID</span><span className="font-mono font-semibold">{viewShot.payment.transaction_id}</span></div>
                <div className="flex justify-between mt-1"><span className="text-slate-500">Amount</span><span className="font-semibold text-[#E65100]">₹{Number(viewShot.amount).toLocaleString("en-IN")}</span></div>
              </div>
              <img src={viewShot.payment.screenshot} alt="payment proof" className="w-full rounded-lg border border-amber-200" />
              <div className="flex gap-2">
                <Button onClick={() => { act(viewShot.reference, "confirm"); setViewShot(null); }} className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="dialog-confirm-btn"><Check className="w-4 h-4 mr-1" /> Confirm</Button>
                <Button onClick={() => { act(viewShot.reference, "cancel"); setViewShot(null); }} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="dialog-cancel-btn"><X className="w-4 h-4 mr-1" /> Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
